// packages/core/src/diff/surfaces.ts
import type { ResolvedSurface } from "../resolver.js";
import type { SurfaceDiff, Status } from "../diff-types.js";
import { diffFields, diffAspects } from "./fields-aspects.js";
import { diffInterfaces } from "./interfaces.js";

export function diffSurfaces(
  before: readonly ResolvedSurface[],
  after: readonly ResolvedSurface[]
): Map<string, SurfaceDiff> {
  const byNameBefore = new Map(before.map((f) => [f.name, f]));
  const byNameAfter = new Map(after.map((f) => [f.name, f]));
  const all = new Set<string>([...byNameBefore.keys(), ...byNameAfter.keys()]);
  const out = new Map<string, SurfaceDiff>();
  for (const name of all) {
    const b = byNameBefore.get(name);
    const a = byNameAfter.get(name);
    if (b && a) out.set(name, matchedSurface(name, b, a));
    else if (a) out.set(name, addedSurface(name, a));
    else if (b) out.set(name, removedSurface(name, b));
  }
  return out;
}

function matchedSurface(name: string, b: ResolvedSurface, a: ResolvedSurface): SurfaceDiff {
  const fields = diffFields(b.body.fields, a.body.fields);
  const aspects = diffAspects(b.body.aspects, a.body.aspects);
  const interfaces = diffInterfaces(b.interfaces, a.interfaces);
  const surfaces = diffSurfaces(b.surfaces, a.surfaces);
  const anyInner =
    fields.size > 0 ||
    aspects.size > 0 ||
    [...interfaces.values()].some((i) => i.status !== "unchanged") ||
    [...surfaces.values()].some((f) => f.status !== "unchanged");
  const status: Status = anyInner ? "modified" : "unchanged";
  return { name, status, fields, aspects, interfaces, surfaces };
}

function addedSurface(name: string, _f: ResolvedSurface): SurfaceDiff {
  return {
    name,
    status: "added",
    fields: new Map(),
    aspects: new Map(),
    interfaces: new Map(),
    surfaces: new Map(),
  };
}

function removedSurface(name: string, _f: ResolvedSurface): SurfaceDiff {
  return {
    name,
    status: "removed",
    fields: new Map(),
    aspects: new Map(),
    interfaces: new Map(),
    surfaces: new Map(),
  };
}
